import { useMemo, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { Checkbox, Loader, Stack, Typography, useSnackbar } from '@lyearn/ui';
import * as ContentListModal from '@/components/ContentListModal';
import { ModalVariant } from '@/components/Modals/types';
import useNavigation from '@/hooks/useNavigation';
import usePagination from '@/hooks/usePagination';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import { getContentUrl } from '@/modules/corporate/modules/path/utils';
import {
  GroupAdditionMethodEnum,
  JourneyStepSessionAttendeeAdditionType,
  UserType,
} from '@/types/schema';

import { useCreateJourneyStepTraineeSessionMutation } from '../../../../graphql/mutations/generated/createJourneyStepTraineeSession';
import { useUpdateJourneyStepTraineeSessionMutation } from '../../../../graphql/mutations/generated/updateJourneyStepTraineeSession';
import { useGetBatchUsersProgressQuery } from '../../../../graphql/queries/generated/getBatchUsersProgress';
import useJourneyContentPerformances from '../../../../hooks/useJourneyContentPerformances';
import { JourneyParticipantProgressModalProps } from '../JourneyParticipantProgressModal';
import UserListItem from './components/UserListItem';

export type SelectedParticipantsType = {
  isSelectAll: boolean;
  userIds: string[];
  excludeUserIds: string[];
};

const ContentConsumptionModal = (props: JourneyParticipantProgressModalProps) => {
  const {
    content,
    onClose,
    batchId,
    journeyStepId,
    isSelectAll = false,
    participantIds = [],
    excludeParticipantIds = [],
    isEditMode = false,
    sessionId,
  } = props;
  const { t } = useTranslation(['Journey']);
  const { id: trainerId } = useUserContext();
  const navigation = useNavigation();
  const [searchText, setSearchText] = useState('');
  const { showSnackbar, showErrorSnackbar } = useSnackbar();
  const [selectedParticipants, setSelectedParticipants] = useState<SelectedParticipantsType>({
    isSelectAll,
    userIds: participantIds ?? [],
    excludeUserIds: excludeParticipantIds ?? [],
  });
  const toggleSelectAll = () => {
    if (selectedParticipants.isSelectAll) {
      setSelectedParticipants({
        isSelectAll: false,
        userIds: [],
        excludeUserIds: [],
      });
    } else {
      setSelectedParticipants({
        isSelectAll: true,
        userIds: [],
        excludeUserIds: [],
      });
    }
  };

  const toggleUserSelection = (userId: string) => {
    if (selectedParticipants.isSelectAll) {
      if (selectedParticipants.excludeUserIds.includes(userId)) {
        setSelectedParticipants((prevState) => ({
          ...prevState,
          excludeUserIds: prevState.excludeUserIds.filter((id) => id !== userId),
        }));
      } else {
        setSelectedParticipants((prevState) => ({
          ...prevState,
          excludeUserIds: [...prevState.excludeUserIds, userId],
        }));
      }
    } else {
      if (selectedParticipants.userIds.includes(userId)) {
        setSelectedParticipants((prevState) => ({
          ...prevState,
          userIds: prevState.userIds.filter((id) => id !== userId),
        }));
      } else {
        setSelectedParticipants((prevState) => ({
          ...prevState,
          userIds: [...prevState.userIds, userId],
          excludeUserIds: prevState.excludeUserIds.filter((id) => id === userId),
        }));
      }
    }
  };

  const {
    data: participants,
    fetchMore,
    totalCount = 0,
    isLoading,
  } = usePagination(
    useGetBatchUsersProgressQuery,
    {
      filters: {
        groupFilter: {
          groupIds: [batchId],
          groupAdditionMethod: GroupAdditionMethodEnum.All,
        },
        searchText,
        journeyStepIdForPersonaView: journeyStepId,
      },
      journeyStepId,
      batchId,
    },
    { edgeKey: 'users', requestPolicy: 'cache-and-network' },
  );

  const selectedCount = useMemo(() => {
    const { isSelectAll, userIds, excludeUserIds } = selectedParticipants;
    return isSelectAll ? totalCount - excludeUserIds.length : userIds.length;
  }, [selectedParticipants, totalCount]);

  const [{ fetching: fetchingSessionId }, createJourneyStepTraineeSessionMutation] =
    useCreateJourneyStepTraineeSessionMutation();
  const [{ fetching: updatingSessionId }, updateJourneyStepTraineeSessionMutation] =
    useUpdateJourneyStepTraineeSessionMutation();

  const onSubmitCreate = async () => {
    const res = await createJourneyStepTraineeSessionMutation({
      input: {
        trainerId,
        journeyStepId,
        batchId,
        entityId: content.id,
        attendeeIds: selectedParticipants.userIds,
        attendeeAdditionType: selectedParticipants.isSelectAll
          ? JourneyStepSessionAttendeeAdditionType.All
          : JourneyStepSessionAttendeeAdditionType.Manual,
        excludeAttendeeIds: selectedParticipants.excludeUserIds,
      },
    });
    if (res.error || !res?.data?.createJourneyStepTraineeSession?.sessionId) {
      showErrorSnackbar({
        message: t('Common:Error.SomeErrorOccurredTryAgain'),
      });
    }
    const newSessionId = res?.data?.createJourneyStepTraineeSession?.sessionId;
    const { view: viewUrl } = getContentUrl({ type: content.type!, id: content.id });
    navigation.push(viewUrl, {
      query: {
        sessionId: newSessionId,
      },
    });
  };

  const onSubmitEdit = async () => {
    const res = await updateJourneyStepTraineeSessionMutation({
      input: {
        attendeeIds: selectedParticipants.userIds,
        attendeeAdditionType: selectedParticipants.isSelectAll
          ? JourneyStepSessionAttendeeAdditionType.All
          : JourneyStepSessionAttendeeAdditionType.Manual,
        excludeAttendeeIds: selectedParticipants.excludeUserIds,
        sessionId: sessionId ?? '',
      },
    });
    if (res.error || !res?.data?.updateJourneyStepTraineeSession) {
      showErrorSnackbar({
        message: t('Common:Error.SomeErrorOccurredTryAgain'),
      });
    } else {
      showSnackbar({ message: t('Journey:EmployeesUpdated') });
      onClose();
    }
  };

  const userIds = participants.map((user) => user.id);

  const { data: contentPerformances } = useJourneyContentPerformances(content.id, userIds, false);

  if (!batchId.length || !journeyStepId.length) {
    return null;
  }

  return (
    <ContentListModal.Main
      action={{
        text: t('Common:Actions.Proceed'),
        discardText: t('Common:Actions.Cancel'),
        onSubmit: isEditMode ? onSubmitEdit : onSubmitCreate,
        isSubmitting: fetchingSessionId || updatingSessionId,
        disabled: fetchingSessionId || updatingSessionId,
      }}
      fetchMore={fetchMore}
      header={{
        title: isEditMode ? t('Journey:EditEmployees') : t('Journey:SelectEmployeesTitle'),
        description: t('Journey:SelectEmployeesDescription'),
      }}
      items={participants as UserType[]}
      searchBar={{
        value: searchText,
        onChange: setSearchText,
      }}
      variant={ModalVariant.Wide}
      onClose={onClose}>
      <Stack
        alignItems="center"
        className="justify-between py-10 mx-32 border-divider border-b-[1px]"
        direction="row">
        <Typography color="text-text-placeholder" variant="label-caps">
          {`${t('Common:Selected')} (${selectedCount ?? 0})`}
        </Typography>
        <Stack className="pr-8">
          <Checkbox checked={selectedParticipants.isSelectAll} onChange={toggleSelectAll} />
        </Stack>
      </Stack>
      {isLoading ? (
        <Stack className="h-full w-full">
          <Loader className="m-auto" size={30} />
        </Stack>
      ) : null}
      <ContentListModal.Body
        className="flex-1 px-16"
        itemRenderer={(index: number) => {
          const user = participants[index];

          return (
            <UserListItem
              key={user.id}
              progress={contentPerformances?.[user.id]?.progress ?? 0}
              selectedParticipants={selectedParticipants}
              user={user}
              onChange={() => {
                toggleUserSelection(user.id);
              }}
            />
          );
        }}></ContentListModal.Body>
    </ContentListModal.Main>
  );
};

export default ContentConsumptionModal;
